/**
 * @file        src/components/AirlineLogo.jsx
 * @description Renders an airline's real logo from /public/airlines/<IATA>.<ext>
 *              (served at /airlines/<code>.<ext>; PNG unless listed in
 *              LOGO_EXT). Falls back to the original
 *              colour-coded 2-letter badge when a code has no logo file or the
 *              image fails to load, so the UI never shows a broken image.
 * @author      Kinetika Labs
 * @created     2026-06-25
 * @changelog
 *   - 2026-06-25 Claude — Initial. Replaces the static 2-letter airline badges
 *                          with the bundled airline logo set.
 *   - 2026-06-25 Claude — Dropped the white background + padding so logos sit
 *                          flush (no white frame/margin around the mark).
 *   - 2026-07-30 Claude — Added LOGO_EXT so a code can ship as SVG instead of
 *                          PNG (Vistara/UK), which stays crisp at every size.
 */
import React, { useState, useEffect } from 'react';

// Codes whose logo file is not a .png. The bundled set is PNG, so this only
// lists the exceptions.
const LOGO_EXT = { UK: 'svg' };

export default function AirlineLogo({ code, color, label, size = 40, className = '' }) {
  const iata = String(code || '').toUpperCase();
  const src = iata ? `/airlines/${iata}.${LOGO_EXT[iata] || 'png'}` : null;
  const [failed, setFailed] = useState(false);

  // Reset the error state when the airline changes (cards are reused across
  // results), otherwise a previous miss would hide a valid logo.
  useEffect(() => { setFailed(false); }, [iata]);

  const box = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={label || iata || 'Airline'}
        title={label || iata}
        onError={() => setFailed(true)}
        draggable="false"
        style={box}
        className={'shrink-0 rounded-lg object-contain ' + className}
      />
    );
  }

  // Fallback: the original colour badge.
  return (
    <div
      style={{ ...box, background: color || '#475569' }}
      className={
        'shrink-0 rounded-lg flex items-center justify-center font-display font-bold text-ink ' +
        className
      }
    >
      {(label || iata || '··').slice(0, 2)}
    </div>
  );
}
